import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from 'react-query'
import { paginationDefault, trimObj } from 'common'
import { api } from '../utils'

const flatMapRecords = (pages, dataKey) =>
  pages.flatMap(({ records, data }) => records || data[dataKey])
const parseRecords = ({ data, labelKey, dataKey }) => {
  const pages = data?.data?.pages ?? data?.pages
  if (pages) {
    return flatMapRecords(pages, dataKey).map(record => ({
      label: record[labelKey],
      value: record,
    }))
  }
  return []
}

export const useAutoCompleteInfiniteQuery = ({
  endpoint,
  dataKey,
  inputValue,
  query,
  labelKey,
  isEnabled = true,
  pageSize = paginationDefault.pageSize,
  cacheTime = 0,
}) => {
  const fetchRecords = useCallback(
    async ({ pageParam = 0 }) => {
      const { data } = await api.get(
        endpoint,
        trimObj({
          ...query,
          search: inputValue,
          page: pageParam,
          pageSize,
        }),
      )

      if (!data) {
        return null
      }
      const { [dataKey]: records, ...rest } = data
      return { ...rest, records }
    },
    [endpoint, inputValue, query, dataKey, pageSize],
  )

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } =
    useInfiniteQuery([`${dataKey}Search`, dataKey, inputValue], fetchRecords, {
      getNextPageParam: (lastPage, allPages) => {
        const allRecords = flatMapRecords(allPages)
        const morePagesExist = lastPage.totalCount > allRecords.length

        return morePagesExist ? allPages.length : false
      },
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      cacheTime,
    })

  const options = useMemo(
    () => parseRecords({ data, dataKey, labelKey }),
    [data, labelKey, dataKey],
  )

  return { options, fetchNextPage, hasNextPage, isLoading, isFetching }
}
